/**
 * Multi-column layout detection.
 *
 * Strategy: build a "column occupancy" histogram — for each x-bucket on the page,
 * count how many TextItems horizontally span it. Columns are separated by contiguous
 * runs of low-occupancy buckets (whitespace gutters) wider than `minGutterRatio`
 * of the page width.
 *
 * Why this works: even in a 7-column flowchart layout (e.g. our 7-deadly-sins page),
 * each column's items pile into a narrow x range; the gaps between columns produce
 * sustained zero-occupancy bands.
 *
 * Why we don't just split by item.x: items inside a column have varying x offsets
 * (indented bullets, sub-text), so x clustering on starts is brittle. Occupancy is
 * geometrically what "a column" actually means.
 */
export function detectColumns(items) {
    // Group items by page; columns are page-local.
    const byPage = new Map();
    for (const it of items) {
        const arr = byPage.get(it.page);
        if (arr)
            arr.push(it);
        else
            byPage.set(it.page, [it]);
    }
    const columns = [];
    for (const [page, pageItems] of byPage) {
        columns.push(...detectColumnsOnPage(page, pageItems));
    }
    return columns;
}
const BUCKET_COUNT = 100;
const MIN_GUTTER_RATIO = 0.025; // gutter must be ≥ 2.5% of page width
const MIN_COLUMN_RATIO = 0.05; // column must be ≥ 5% of page width
const MIN_ITEMS_FOR_MULTI_COLUMN = 8; // pages with very few items stay single-column
function detectColumnsOnPage(page, items) {
    if (items.length < MIN_ITEMS_FOR_MULTI_COLUMN) {
        return [singleColumn(page, items)];
    }
    // Page x range: tight to actual content, not the abstract page width.
    const xMin = Math.min(...items.map((it) => it.x));
    const xMax = Math.max(...items.map((it) => it.xEnd));
    const pageWidth = xMax - xMin;
    if (pageWidth <= 0)
        return [singleColumn(page, items)];
    const bucketWidth = pageWidth / BUCKET_COUNT;
    const occupancy = new Array(BUCKET_COUNT).fill(0);
    for (const it of items) {
        const startBucket = Math.max(0, Math.floor((it.x - xMin) / bucketWidth));
        const endBucket = Math.min(BUCKET_COUNT - 1, Math.floor((it.xEnd - xMin) / bucketWidth));
        for (let b = startBucket; b <= endBucket; b++) {
            occupancy[b] = (occupancy[b] ?? 0) + 1;
        }
    }
    // Identify gutters: contiguous low-occupancy runs.
    // A gutter must be ≥ MIN_GUTTER_RATIO buckets AND have occupancy ≤ threshold.
    const gutterMinBuckets = Math.max(2, Math.floor(BUCKET_COUNT * MIN_GUTTER_RATIO));
    // Threshold: more lenient (zero occupancy) for shorter gutters; stricter (≤1) for wider gutters.
    // Empirically zero-occupancy is the cleanest signal.
    const gutters = []; // inclusive [start,end]
    let runStart = -1;
    for (let b = 0; b < BUCKET_COUNT; b++) {
        if ((occupancy[b] ?? 0) === 0) {
            if (runStart === -1)
                runStart = b;
        }
        else {
            if (runStart !== -1 && b - runStart >= gutterMinBuckets) {
                gutters.push([runStart, b - 1]);
            }
            runStart = -1;
        }
    }
    if (runStart !== -1 && BUCKET_COUNT - runStart >= gutterMinBuckets) {
        gutters.push([runStart, BUCKET_COUNT - 1]);
    }
    // Drop gutters that touch page edges (those are margins, not column splits).
    const interiorGutters = gutters.filter(([s, e]) => s > 0 && e < BUCKET_COUNT - 1);
    if (interiorGutters.length === 0) {
        return [{ page, xMin, xMax, items: items.slice() }];
    }
    // Build column ranges between gutters.
    const columnMinBuckets = Math.max(3, Math.floor(BUCKET_COUNT * MIN_COLUMN_RATIO));
    const ranges = [];
    let cursor = 0;
    for (const [gStart, gEnd] of interiorGutters) {
        if (gStart - cursor >= columnMinBuckets)
            ranges.push([cursor, gStart - 1]);
        cursor = gEnd + 1;
    }
    if (BUCKET_COUNT - cursor >= columnMinBuckets)
        ranges.push([cursor, BUCKET_COUNT - 1]);
    // If we dropped everything (unlikely), fall back to single column.
    if (ranges.length <= 1) {
        return [{ page, xMin, xMax, items: items.slice() }];
    }
    // Assign each item to the column whose range contains its x midpoint.
    const cols = ranges.map(([s, e]) => ({
        page,
        xMin: xMin + s * bucketWidth,
        xMax: xMin + (e + 1) * bucketWidth,
        items: [],
    }));
    for (const it of items) {
        const mid = (it.x + it.xEnd) / 2;
        let assigned = cols.length - 1;
        for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            if (mid <= col.xMax) {
                assigned = i;
                break;
            }
        }
        cols[assigned].items.push(it);
    }
    // Drop empty columns (defensive).
    return cols.filter((c) => c.items.length > 0);
}
function singleColumn(page, items) {
    if (items.length === 0) {
        return { page, xMin: 0, xMax: 0, items: [] };
    }
    return {
        page,
        xMin: Math.min(...items.map((it) => it.x)),
        xMax: Math.max(...items.map((it) => it.xEnd)),
        items: items.slice(),
    };
}
//# sourceMappingURL=columns.js.map